import {
  Accordion,
  Badge,
  Box,
  Button,
  Center,
  Group,
  LoadingOverlay,
  Modal,
  Pagination,
  Stack,
  Tabs,
  Text,
} from '@mantine/core';
import { semanticJurisdiction } from '@/lib/helpers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GenericTable,
  GenericTableColumn,
} from '@/components/GenericTable/GenericTable';
import { useConfirmation } from '@/hooks/useConfirmation';
import {
  RenewalListParams,
  RenewalStatus,
  TDeliveryService,
  TEligibleShare,
  TRenewal,
  useCancelRenewal,
  useDeliverService,
  useDeliveryStatus,
  useEligibleShares,
  useRenewalList,
} from '@/models/renewals';
import { useUrlState } from '@/hooks/useUrlState';
import { urlField } from '@/lib/urlState';
import RenewalDetailsModal from './RenewalDetailsModal';
import classes from './RenewalsPage.module.css';

type RenewalSortField = NonNullable<RenewalListParams['sortBy']>;

const RENEWALS_SCHEMA = {
  tab: urlField.enum(
    ['delivery', 'all', 'pending', 'invoiced', 'paid', 'cancelled'] as const,
    'delivery',
  ),
  page: urlField.page(),
  sortBy: urlField.enum<RenewalSortField>(
    ['created_at', 'renewal_date', 'issue_date', 'due_date'],
    'renewal_date',
  ),
  sortOrder: urlField.enum(['asc', 'desc'] as const, 'asc'),
};

const STATUS_COLORS: Record<RenewalStatus, string> = {
  pending: 'blue',
  invoiced: 'orange',
  paid: 'green',
  cancelled: 'red',
};

const STATUS_LABELS: Record<RenewalStatus, string> = {
  pending: 'Pending',
  invoiced: 'Invoiced',
  paid: 'Paid',
  cancelled: 'Cancelled',
};

function formatDate(date: string | null) {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(date: string | null) {
  if (!date) return '-';
  return new Date(date).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatAmount(amount: number | null, currency: string | null) {
  if (amount == null || currency == null) return '-';
  return `${Number(amount).toFixed(2)} ${currency}`;
}

type FlatService = TDeliveryService & { id: number; blockTitle: string | null };

const COUNTDOWN_SECONDS = 10;

function DeliverButton({
  offerId,
  serviceId,
  offerItemId,
  serviceName,
}: {
  offerId: number;
  serviceId: number;
  offerItemId: number;
  serviceName: string;
}) {
  const deliverMutation = useDeliverService();
  const { confirm, ConfirmationDialog } = useConfirmation();
  const [phase, setPhase] = useState<'idle' | 'countdown'>('idle');
  const [seconds, setSeconds] = useState(COUNTDOWN_SECONDS);
  const timerRef = useRef<number>(undefined);
  const intervalRef = useRef<number>(undefined);

  const cancelCountdown = useCallback(() => {
    clearTimeout(timerRef.current);
    clearInterval(intervalRef.current);
    setPhase('idle');
    setSeconds(COUNTDOWN_SECONDS);
  }, []);

  const startCountdown = useCallback(() => {
    setPhase('countdown');
    setSeconds(COUNTDOWN_SECONDS);
    intervalRef.current = window.setInterval(
      () => setSeconds((s) => s - 1),
      1000,
    );
    timerRef.current = window.setTimeout(async () => {
      clearInterval(intervalRef.current);
      setPhase('idle');
      deliverMutation.mutate({ offerId, serviceId, offerItemId });
    }, COUNTDOWN_SECONDS * 1000);
  }, [offerId, serviceId, offerItemId, deliverMutation]);

  const handleClick = useCallback(async () => {
    const ok = await confirm({
      title: 'Mark Service as Delivered',
      message: `Mark "${serviceName}" as delivered? This action is irreversible.`,
      confirmButtonText: 'Deliver',
    });
    if (ok) startCountdown();
  }, [serviceName, startCountdown, confirm]);

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      clearInterval(intervalRef.current);
    };
  }, []);

  if (phase === 'countdown') {
    return (
      <Button
        size="compact-xs"
        color="gray"
        variant="light"
        onClick={cancelCountdown}
        style={{ position: 'relative', overflow: 'hidden' }}
      >
        <Box className={classes.countdownOverlay} />
        Undo ({seconds}s)
      </Button>
    );
  }

  return (
    <>
      <ConfirmationDialog />
      <Button
        size="compact-xs"
        variant="light"
        color="teal"
        onClick={handleClick}
        loading={deliverMutation.isPending}
      >
        Deliver
      </Button>
    </>
  );
}

function ShareDeliveryPanel({ share }: { share: TEligibleShare }) {
  const { data, isLoading } = useDeliveryStatus(share.offerId);

  const services = useMemo<FlatService[]>(() => {
    if (!data) return [];
    const result: FlatService[] = [];
    for (const block of data.blocks ?? []) {
      for (const svc of block.services) {
        result.push({
          ...svc,
          id: svc.serviceId,
          blockTitle: block.blockTitle,
        });
      }
    }
    for (const svc of data.standaloneServices ?? []) {
      result.push({ ...svc, id: svc.serviceId, blockTitle: null });
    }
    return result;
  }, [data]);

  const columns: GenericTableColumn<FlatService>[] = [
    {
      accessor: 'serviceName',
      title: 'Service',
      render: (r) => (
        <Text fz="xs" pl={r.parentId ? 'lg' : 0}>
          {r.parentId ? '└ ' : ''}
          {r.serviceName}
        </Text>
      ),
    },
    {
      accessor: 'blockTitle',
      title: 'Block',
      render: (r) => (
        <Text fz="xs" c="dimmed">
          {r.blockTitle ?? 'Standalone'}
        </Text>
      ),
    },
    {
      accessor: 'isRenewable',
      title: 'Renewable',
      width: 90,
      render: (r) =>
        r.isRenewable ? (
          <Badge size="xs" color="blue" variant="light">
            Yes
          </Badge>
        ) : (
          <Text fz="xs" c="dimmed">
            No
          </Text>
        ),
    },
    {
      accessor: 'deliveredAt',
      title: 'Status',
      width: 100,
      render: (r) =>
        !r.isRenewable ? (
          <Text fz="xs" c="dimmed">
            -
          </Text>
        ) : r.deliveredAt ? (
          <Badge size="xs" color="green" variant="light">
            Delivered
          </Badge>
        ) : (
          <Badge size="xs" color="gray" variant="light">
            Pending
          </Badge>
        ),
    },
    {
      accessor: 'deliveredByName',
      title: 'Delivered',
      render: (r) =>
        r.deliveredAt ? (
          <Stack gap={0}>
            <Text fz="xs">{formatDateTime(r.deliveredAt)}</Text>
            <Text fz="xs" c="dimmed">
              {r.deliveredByName}
            </Text>
          </Stack>
        ) : (
          <Text fz="xs" c="dimmed">
            -
          </Text>
        ),
    },
    {
      accessor: 'deliveredBy',
      title: 'Action',
      width: 130,
      render: (r) =>
        r.isRenewable && !r.deliveredAt && r.offerItemId != null ? (
          <DeliverButton
            offerId={share.offerId}
            serviceId={r.serviceId}
            offerItemId={r.offerItemId}
            serviceName={r.serviceName}
          />
        ) : null,
    },
  ];

  return (
    <Box pos="relative" mih={60}>
      {data?.anchorDate && (
        <Group gap="xs" mb="sm">
          <Text fz="xs" c="dimmed">
            Anchor Date:
          </Text>
          <Badge size="sm" variant="light" color="teal">
            {formatDate(data.anchorDate)}
          </Badge>
        </Group>
      )}
      <GenericTable
        records={services}
        columns={columns}
        loading={isLoading}
        emptyMessage="No services found"
        idAccessor="id"
      />
    </Box>
  );
}

function DeliveryTab() {
  const { data, isLoading } = useEligibleShares();
  const shares = data?.shares || [];

  return (
    <Box pos="relative" mt="md" mih={100}>
      <LoadingOverlay visible={isLoading} />
      {shares.length === 0 && !isLoading && (
        <Text fz="sm" c="dimmed" ta="center" py="xl">
          No eligible shares found. Shares appear here after initial invoice is
          paid.
        </Text>
      )}
      {shares.length > 0 && (
        <Accordion variant="separated">
          {shares.map((share) => (
            <Accordion.Item key={share.offerId} value={String(share.offerId)}>
              <Accordion.Control>
                <Group justify="space-between" wrap="nowrap">
                  <Stack gap={0}>
                    <Group gap="xs">
                      <Text fz="sm" fw={500}>
                        {share.contactName}
                      </Text>
                      <Text fz="xs" c="dimmed">
                        {share.contactEmail}
                      </Text>
                    </Group>
                    <Group gap="xs">
                      <Text fz="xs">{share.proposalName}</Text>
                      {share.jurisdiction && (
                        <Text fz="xs" c="dimmed">
                          ({semanticJurisdiction(share.jurisdiction)})
                        </Text>
                      )}
                    </Group>
                  </Stack>
                  <Group gap="sm">
                    <Badge
                      size="sm"
                      variant="light"
                      color={share.deliveredServices > 0 ? 'teal' : 'gray'}
                    >
                      {share.deliveredServices}/{share.totalServices} delivered
                    </Badge>
                    {share.anchorDate && (
                      <Badge size="sm" variant="light" color="blue">
                        Anchor: {formatDate(share.anchorDate)}
                      </Badge>
                    )}
                  </Group>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <ShareDeliveryPanel share={share} />
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </Box>
  );
}

export default function RenewalsPage() {
  const [state, setState] = useUrlState(RENEWALS_SCHEMA);
  const activeTab = state.tab;
  const currentPage = state.page;
  const sortBy = state.sortBy;
  const sortOrder = state.sortOrder;
  const [selectedRenewalId, setSelectedRenewalId] = useState<number | null>(
    null,
  );
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [cancelConfirmId, setCancelConfirmId] = useState<number | null>(null);

  const isRenewalTab = activeTab !== 'delivery';

  const params: RenewalListParams = {
    page: currentPage,
    limit: 20,
    status: (isRenewalTab ? activeTab : 'all') as RenewalStatus | 'all',
    sortBy,
    sortOrder,
  };

  const { data, isLoading } = useRenewalList(params);
  const cancelMutation = useCancelRenewal();

  const renewals = data?.renewals || [];
  const statusCounts = data?.statusCounts || {
    all: 0,
    pending: 0,
    invoiced: 0,
    paid: 0,
    cancelled: 0,
  };

  const handleSort = (field: string) => {
    const validFields: RenewalListParams['sortBy'][] = [
      'created_at',
      'renewal_date',
      'issue_date',
      'due_date',
    ];
    if (!validFields.includes(field as any)) return;
    setState(
      {
        sortBy: field as RenewalSortField,
        sortOrder: sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc',
      },
      { resetPage: false },
    );
  };

  const handleCancel = () => {
    if (cancelConfirmId) {
      cancelMutation.mutate(cancelConfirmId, {
        onSuccess: () => setCancelConfirmId(null),
      });
    }
  };

  const columns: GenericTableColumn<TRenewal>[] = [
    {
      accessor: 'id',
      title: '#',
      width: 60,
      render: (r) => <Text fz="xs">#{r.id}</Text>,
    },
    {
      accessor: 'contactName',
      title: 'Client',
      render: (r) => (
        <Stack gap={0}>
          <Text fz="xs" fw={500}>
            {r.contactName}
          </Text>
          <Text fz="xs" c="dimmed">
            {r.contactEmail}
          </Text>
        </Stack>
      ),
    },
    {
      accessor: 'proposalName',
      title: 'Proposal',
      render: (r) => (
        <Stack gap={0}>
          <Text fz="xs">{r.proposalName}</Text>
          {r.jurisdiction && (
            <Text fz="xs" c="dimmed">
              {semanticJurisdiction(r.jurisdiction)}
            </Text>
          )}
        </Stack>
      ),
    },
    {
      accessor: 'cycleNumber',
      title: 'Cycle',
      width: 60,
      render: (r) => <Text fz="xs">{r.cycleNumber}</Text>,
    },
    {
      accessor: 'status',
      title: 'Status',
      width: 100,
      render: (r) => (
        <Badge size="sm" color={STATUS_COLORS[r.status]} variant="light">
          {STATUS_LABELS[r.status]}
        </Badge>
      ),
    },
    {
      accessor: 'renewalDate',
      title: 'Renewal Date',
      sortable: true,
      render: (r) => <Text fz="xs">{formatDate(r.renewalDate)}</Text>,
    },
    {
      accessor: 'issueDate',
      title: 'Issue Date',
      sortable: true,
      render: (r) => <Text fz="xs">{formatDate(r.issueDate)}</Text>,
    },
    {
      accessor: 'dueDate',
      title: 'Due Date',
      sortable: true,
      render: (r) => <Text fz="xs">{formatDate(r.dueDate)}</Text>,
    },
    {
      accessor: 'invoiceNumber',
      title: 'Invoice',
      render: (r) =>
        r.invoiceNumber ? (
          <Stack gap={0}>
            <Text fz="xs">{r.invoiceNumber}</Text>
            <Text fz="xs" c="dimmed">
              {formatAmount(r.invoiceAmount, r.invoiceCurrency)}
            </Text>
          </Stack>
        ) : (
          <Text fz="xs" c="dimmed">
            -
          </Text>
        ),
    },
    {
      accessor: 'createdAt',
      title: 'Actions',
      width: 120,
      render: (r) => (
        <Group gap="xs">
          <Button
            size="compact-xs"
            variant="subtle"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRenewalId(r.id);
              setDetailsOpen(true);
            }}
          >
            Details
          </Button>
          {(r.status === 'pending' || r.status === 'invoiced') && (
            <Button
              size="compact-xs"
              variant="subtle"
              color="red"
              onClick={(e) => {
                e.stopPropagation();
                setCancelConfirmId(r.id);
              }}
            >
              Cancel
            </Button>
          )}
        </Group>
      ),
    },
  ];

  const renewalTabContent = (
    <Box pos="relative" mt="md">
      <LoadingOverlay visible={isLoading} />
      <Stack gap="md">
        <GenericTable
          records={renewals}
          columns={columns}
          loading={isLoading}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          emptyMessage="No renewals found."
        />
        {data && data.total > 0 && (
          <Center>
            <Pagination
              value={currentPage}
              onChange={(page) => setState({ page })}
              total={data.totalPages}
              size="sm"
            />
          </Center>
        )}
      </Stack>
    </Box>
  );

  return (
    <Box p="md">
      <Stack gap="md">
        <Tabs
          value={activeTab}
          onChange={(value) =>
            setState({ tab: (value || 'delivery') as typeof state.tab })
          }
        >
          <Tabs.List>
            <Tabs.Tab value="delivery">
              <Text fz="xs">Service Delivery</Text>
            </Tabs.Tab>
            <Tabs.Tab value="all">
              <Text fz="xs">All ({statusCounts.all})</Text>
            </Tabs.Tab>
            <Tabs.Tab value="pending">
              <Text fz="xs" component="span">
                <Badge size="xs" color="blue" variant="light" mr={4}>
                  {statusCounts.pending}
                </Badge>
                Pending
              </Text>
            </Tabs.Tab>
            <Tabs.Tab value="invoiced">
              <Text fz="xs" component="span">
                <Badge size="xs" color="orange" variant="light" mr={4}>
                  {statusCounts.invoiced}
                </Badge>
                Invoiced
              </Text>
            </Tabs.Tab>
            <Tabs.Tab value="paid">
              <Text fz="xs" component="span">
                <Badge size="xs" color="green" variant="light" mr={4}>
                  {statusCounts.paid}
                </Badge>
                Paid
              </Text>
            </Tabs.Tab>
            <Tabs.Tab value="cancelled">
              <Text fz="xs" component="span">
                <Badge size="xs" color="red" variant="light" mr={4}>
                  {statusCounts.cancelled}
                </Badge>
                Cancelled
              </Text>
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="delivery">
            <DeliveryTab />
          </Tabs.Panel>
          <Tabs.Panel value="all">{renewalTabContent}</Tabs.Panel>
          <Tabs.Panel value="pending">{renewalTabContent}</Tabs.Panel>
          <Tabs.Panel value="invoiced">{renewalTabContent}</Tabs.Panel>
          <Tabs.Panel value="paid">{renewalTabContent}</Tabs.Panel>
          <Tabs.Panel value="cancelled">{renewalTabContent}</Tabs.Panel>
        </Tabs>
      </Stack>

      <RenewalDetailsModal
        renewalId={selectedRenewalId}
        opened={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedRenewalId(null);
        }}
      />

      <Modal
        opened={cancelConfirmId !== null}
        onClose={() => setCancelConfirmId(null)}
        title="Cancel Renewal"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to cancel this renewal? If an unpaid invoice
            exists, it will also be cancelled. This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setCancelConfirmId(null)}>
              No, keep it
            </Button>
            <Button
              color="red"
              onClick={handleCancel}
              loading={cancelMutation.isPending}
            >
              Yes, cancel
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
